import {
  DEPENDENCY_SOURCES,
  type DependencySource,
} from "../../../shared/workflowDependencies";

type DependencyBranchInput = {
  name: string;
  headCommit?: {
    dependencies?: ReadonlyArray<{
      name: string;
      version: string | null;
      source: string;
    }>;
  } | null;
};

export type DependencyWorkflowInput = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed?: string | null;
  pausedAt?: number | null;
  branches?: DependencyBranchInput | ReadonlyArray<DependencyBranchInput>;
};

export type WorkflowDependency = {
  name: string;
  version: string | null;
  source: DependencySource;
};

export type WorkflowDependencyRow = {
  workflowId: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  dependencies: WorkflowDependency[];
};

export type DependencySummaryRow = {
  name: string;
  versions: string[];
  workflowCount: number;
  workflowIds: string[];
  source: DependencySource;
};

function getMainBranch(workflow: DependencyWorkflowInput) {
  const branches = workflow.branches;
  if (!branches) return null;
  const list = Array.isArray(branches) ? branches : [branches];
  return list.find((b) => b.name === "main") ?? null;
}

export function computeWorkflowDependencies(
  workflows: ReadonlyArray<DependencyWorkflowInput>
): WorkflowDependencyRow[] {
  const rows: WorkflowDependencyRow[] = [];

  for (const workflow of workflows) {
    const commitDeps = getMainBranch(workflow)?.headCommit?.dependencies ?? [];
    if (commitDeps.length === 0) continue;

    const deps: WorkflowDependency[] = [];
    const seen = new Set<string>();
    for (const source of DEPENDENCY_SOURCES) {
      for (const dep of commitDeps) {
        if (dep.source !== source) continue;
        const key = `${source}:${dep.name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        deps.push({ name: dep.name, version: dep.version, source });
      }
    }

    rows.push({
      workflowId: workflow.id,
      name: workflow.name,
      spaceId: workflow.spaceId,
      badgeSeed: workflow.badgeSeed ?? null,
      pausedAt: workflow.pausedAt ?? null,
      dependencies: deps,
    });
  }

  return rows.sort(
    (a, b) =>
      b.dependencies.length - a.dependencies.length ||
      a.name.localeCompare(b.name)
  );
}

export function computeDependencySummary(
  rows: ReadonlyArray<WorkflowDependencyRow>
): DependencySummaryRow[] {
  const byName = new Map<
    string,
    {
      versions: Set<string>;
      workflowIds: Set<string>;
      source: WorkflowDependency["source"];
    }
  >();

  for (const row of rows) {
    for (const dep of row.dependencies) {
      const entry = byName.get(dep.name) ?? {
        versions: new Set(),
        workflowIds: new Set(),
        source: dep.source,
      };
      if (dep.version) entry.versions.add(dep.version);
      entry.workflowIds.add(row.workflowId);
      byName.set(dep.name, entry);
    }
  }

  return [...byName.entries()]
    .map(([name, data]) => ({
      name,
      versions: [...data.versions].sort(),
      workflowCount: data.workflowIds.size,
      workflowIds: [...data.workflowIds],
      source: data.source,
    }))
    .sort(
      (a, b) =>
        b.workflowCount - a.workflowCount || a.name.localeCompare(b.name)
    );
}
